import type { AxiosError, GenericAbortSignal } from 'axios';
import axios from 'axios';

import i18next from '@/i18n';
import { ROUTES } from '@/shared/routes';
import { getAppNavigate } from '@/shared/services/helpers/navigate-helper';
import {
  getUserToken,
  removeUserToken,
  setUserToken
} from '@/shared/services/user-hash';
import { closeWebView, ssoAuthorize } from '@/shared/webview/base-actions';
import { isInWebView } from '@/shared/webview/env';

export type CustomAxiosError = AxiosError<{
  message?: string;
  statusCode: number;
  retryAfterSeconds?: number;
}>;

export class ApiConnector {
  ssoJwtToken: string | null = null;

  // Single-flight SSO-авторизации: пачка параллельных запросов, словивших 401,
  // дёргает нативу один раз, а не каждый по отдельности (см. authorizeOnce).
  private ssoAuthorizePromise: ReturnType<typeof ssoAuthorize> | null = null;

  private authorizeOnce(): ReturnType<typeof ssoAuthorize> {
    if (!this.ssoAuthorizePromise) {
      this.ssoAuthorizePromise = ssoAuthorize().finally(() => {
        this.ssoAuthorizePromise = null;
      });
    }
    return this.ssoAuthorizePromise;
  }

  isSsoAuthorized(): boolean {
    return !!this.ssoJwtToken;
  }

  /**
   * SSO-авторизация: обменивает суперапп-сессию нативы на JWT и сохраняет его
   * в `ssoJwtToken`, чтобы последующие `call()` уходили уже авторизованными.
   * Можно вызывать проактивно — заранее, в местах, где авторизация точно
   * понадобится, тогда первый запрос не словит 401. Single-flight: параллельные
   * вызовы и ретрай из `call()` ждут один и тот же обмен с нативой.
   *
   * @returns `true`, если токен получен.
   */
  async ssoAuthorize(): Promise<boolean> {
    const response = await this.authorizeOnce();
    if (response) {
      this.ssoJwtToken = response.data.accessToken;
      return true;
    }
    return false;
  }

  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    retried = false
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    retried?: boolean;
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    try {
      const user = getUserToken();
      const headers: Record<string, string> = {};
      // В WebView токен приходит через sso/auth (ssoJwtToken) и user-токена
      // может не быть, поэтому гейтим заголовок на сам bearer, а не на user.
      const bearer = isInWebView() ? this.ssoJwtToken : user?.accessToken;
      if (bearer) headers.Authorization = `Bearer ${bearer}`;

      const { data } = await axios(`${process.env.PUBLIC_API_URL}/` + path, {
        method,
        data: body,
        params: clonedParams,
        headers,
        signal: abortSignal
      });
      return data;
    } catch (e: unknown) {
      const error = e as CustomAxiosError;

      if (error?.response?.status === 401) {
        if (isInWebView()) {
          if (!retried) {
            // Ретраим только один раз: если запрос с обновлённым токеном снова
            // отдал 401, повторная sso-авторизация ничего не изменит — иначе
            // получим бесконечный цикл 401 → ssoAuthorize → call.
            this.ssoJwtToken = null; // протухший токен сбрасываем
            if (await this.ssoAuthorize()) {
              return this.call({
                path,
                method,
                body,
                params,
                abortSignal,
                retried: true
              });
            }
          }
          // В WebView страницы логина нет — на терминальном 401 закрываем мини-апп
          // (натива переавторизует пользователя), а не редиректим в никуда.
          closeWebView({
            type: 'error',
            message: i18next.t('auth.notifications.loginErrorTitle')
          });
        } else {
          const navigate = getAppNavigate();
          removeUserToken();
          const newPath = `/${ROUTES.login}`;
          const savedUrl =
            window.location.pathname !== `/${i18next.language}${newPath}` &&
            `${window.location.pathname}${window.location.search}`;
          navigate(newPath, { state: { savedUrl } });
        }
      }

      const statusCode =
        error?.response?.data?.statusCode || error?.response?.status;
      let message = i18next.t('errors.generic');
      if (statusCode && statusCode !== 404 && statusCode < 500) {
        const rawMessage = error?.response?.data?.message;
        if (Array.isArray(rawMessage)) {
          message = rawMessage.filter(Boolean).join('. ') || message;
        } else if (rawMessage) {
          message = rawMessage;
        }
      }

      throw {
        message,
        statusCode,
        retryAfterSeconds: error?.response?.data?.retryAfterSeconds,
        isCancelled: axios.isCancel(e)
      };
    }
  }

  async refreshToken(): Promise<void> {
    const { refreshToken } = getUserToken();

    if (!refreshToken) {
      return;
    }

    const { data } = await axios(
      `${process.env.PUBLIC_API_URL}/client-portal/auth/refresh`,
      {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          refreshToken
        }
      }
    );

    if (data?.accessToken && data?.refreshToken) {
      setUserToken({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken
      });
    }
  }

  async login(body: ILoginRequest): Promise<TLoginResponse> {
    const response = await this.call<ILoginRequest, TLoginResponse>({
      path: 'client-portal/auth/signin',
      method: 'post',
      body
    });

    if (!response.requirePasswordChange) {
      setUserToken({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken
      });
    }

    return response;
  }

  async handoffToken(token: string): Promise<IHandoffTokenResponse> {
    const response = await this.call<{ token: string }, IHandoffTokenResponse>({
      path: 'client-portal/auth/handoff',
      method: 'post',
      body: { token }
    });

    if (response.accessToken && response.refreshToken) {
      setUserToken({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken
      });
    }

    return response;
  }

  changePassword(
    body: IChangePasswordRequest,
    tempToken?: string
  ): Promise<IChangePasswordResponse> {
    if (tempToken) {
      setUserToken({
        accessToken: tempToken,
        refreshToken: ''
      });
    }

    try {
      return this.call({
        path: 'client-portal/auth/change-password',
        method: 'post',
        body
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  requestPasswordReset(
    body: IRequestPasswordResetRequest
  ): Promise<IRequestPasswordResetResponse> {
    return this.call({
      path: 'client-portal/auth/password-reset/request',
      method: 'post',
      body
    });
  }

  verifyPasswordResetOtp(
    body: IVerifyPasswordResetOtpRequest
  ): Promise<IVerifyPasswordResetOtpResponse> {
    return this.call({
      path: 'client-portal/auth/password-reset/verify',
      method: 'post',
      body
    });
  }

  completePasswordReset(
    body: ICompletePasswordResetRequest
  ): Promise<ICompletePasswordResetResponse> {
    return this.call({
      path: 'client-portal/auth/password-reset/complete',
      method: 'post',
      body
    });
  }

  downloadFile(
    uuid: string
  ): Promise<{ base64: string; name: string; blob?: () => Promise<Blob> }> {
    return this.call({ path: `files/${uuid}/downloadmanually` });
  }
}

export const apiConnector = new ApiConnector();

interface ILoginRequest {
  login: string;
  password: string;
}

type TLoginResponse =
  | {
      requirePasswordChange: true;
      tempToken: string;
    }
  | {
      requirePasswordChange: false;
      accessToken: string;
      refreshToken: string;
    };

interface IChangePasswordRequest {
  newPassword: string;
  repeatNewPassword: string;
}

interface IChangePasswordResponse {
  accessToken: string;
  refreshToken: string;
  message: string;
}

interface IRequestPasswordResetRequest {
  phone: string;
}

interface IRequestPasswordResetResponse {
  message: string;
  retryAfterSeconds: number;
}

interface IVerifyPasswordResetOtpRequest {
  phone: string;
  otp: string;
}

interface IVerifyPasswordResetOtpResponse {
  message: string;
}

interface ICompletePasswordResetRequest {
  phone: string;
  otp: string;
  newPassword: string;
  repeatNewPassword: string;
}

interface ICompletePasswordResetResponse {
  accessToken: string;
  refreshToken: string;
  message: string;
}

interface IHandoffTokenResponse {
  accessToken: string;
  refreshToken: string;
}
